import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitTransferRequestStatusEnumType } from '__generated__/graphql';
import { FC, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';
import SectionCard from 'components/SectionCard';

import Alert from 'primitives/Alert';
import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferStatusToReadable from 'utils/enums/unit-transfer-status-to-readable';

const UNIT_TRANSFER_ACKNOWLEDGEMENT_DETAIL_QUERY = gql(`
  query UnitTransferAcknowledgementDetail($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      reason
      acknowledgedAt
      transferor {
        id
        name
      }
      schemes {
        id
        numberOfUnits
      }
    }
    dashboard {
      id
      investmentEntities {
        id
        name
      }
    }
  }
`);

const ACKNOWLEDGE_UNIT_TRANSFER_REQUEST_MUTATION = gql(`
  mutation AcknowledgeUnitTransferRequest(
    $id: ID!
    $accept: Boolean!
    $investmentEntityId: ID
    $note: String
  ) {
    acknowledgeUnitTransferRequest(
      id: $id
      accept: $accept
      investmentEntityId: $investmentEntityId
      note: $note
    ) {
      id
      status
    }
  }
`);

const ACCEPT = 'ACCEPT';
const DECLINE = 'DECLINE';

type AcknowledgementFormData = {
  response: typeof ACCEPT | typeof DECLINE | '';
  investmentEntityId: string;
  note: string;
};

const UnitTransferAcknowledgement: FC = () => {
  const { requestId } = useParams<{ requestId: string }>() as { requestId: string };
  const navigate = useNavigate();

  // A validated form submission parks here so the confirmation dialog can describe the choice
  // the transferee actually made — declining is terminal and must not read like accepting.
  const [pendingResponse, setPendingResponse] = useState<AcknowledgementFormData | null>(null);
  const formPanelRef = useRef<any>(null);

  const { data, loading, error, refetch } = useQuery(UNIT_TRANSFER_ACKNOWLEDGEMENT_DETAIL_QUERY, {
    variables: { id: requestId },
  });

  const [acknowledgeRequest, { loading: acknowledging, error: acknowledgeError }] = useMutation(
    ACKNOWLEDGE_UNIT_TRANSFER_REQUEST_MUTATION
  );

  function confirmResponse() {
    if (!pendingResponse) return;
    const isAccepting = pendingResponse.response === ACCEPT;

    acknowledgeRequest({
      variables: {
        id: requestId,
        accept: isAccepting,
        // The backend rejects an accept without a receiving entity (VAL_UT_006); it must never
        // be sent on a decline, where no entity was collected.
        investmentEntityId: isAccepting ? pendingResponse.investmentEntityId : undefined,
        note: pendingResponse.note || undefined,
      },
    }).then(result => {
      setPendingResponse(null);
      // Declining used to leave the transferee sitting on a dead form; both paths now land on
      // the tracker, which explains what happens next.
      if (result.data?.acknowledgeUnitTransferRequest) {
        navigate(`/invest/unit-transfers/${requestId}`);
      }
    });
  }

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const request = data.unitTransferRequest;
    const totalUnits = request.schemes.reduce((sum, scheme) => sum + scheme.numberOfUnits, 0);
    const investmentEntities = data.dashboard.investmentEntities;

    const alreadyResponded =
      !!request.acknowledgedAt ||
      request.status !== UnitTransferRequestStatusEnumType.PendingTransfereeAcknowledgement;

    if (alreadyResponded) {
      return (
        <div className="space-y-4">
          <Alert
            type="info"
            title="You've already responded to this request"
            message={`Its current status is ${unitTransferStatusToReadable(request.status).label}.`}
          />
          <Button
            variant="secondary"
            onClick={() => navigate(`/invest/unit-transfers/${request.id}`)}
          >
            View request
          </Button>
        </div>
      );
    }

    const isDeclining = pendingResponse?.response === DECLINE;

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{request.transferor.name}</span> wants to
          transfer their units to you. Review the details below before responding.
        </p>

        <DetailsPanel
          title="Transfer details"
          displayHorizontally
          data={[
            { label: 'From', value: request.transferor.name },
            { label: 'Reason', value: unitTransferReasonToReadable(request.reason) },
            { label: 'Schemes', value: request.schemes.length, type: 'NUMBER' },
            { label: 'Total units', value: totalUnits, type: 'NUMBER' },
          ]}
        />

        <SectionCard title="Your response">
          <FormPanel
            loading={acknowledging}
            error={acknowledgeError}
            buttonRef={formPanelRef}
            onSubmit={(formData: AcknowledgementFormData) => setPendingResponse(formData)}
          >
            <FormInput
              fullWidth
              type="radio-group"
              fieldName="response"
              label="How would you like to respond? *"
              defaultValue=""
              options={[
                { label: 'Accept — credit these units to one of my entities', value: ACCEPT },
                { label: 'Decline — I do not want to receive these units', value: DECLINE },
              ]}
              validators={{ required: true }}
            />
            <FormInput
              fullWidth
              type="select"
              fieldName="investmentEntityId"
              label="Receiving investment entity *"
              helperText="The units will be credited to this entity"
              defaultValue=""
              options={investmentEntities.map(entity => ({
                label: entity.name,
                value: entity.id,
              }))}
              // FormInput drops `required` while a field is hidden, so declining never trips
              // the entity validator.
              conditionsToShow={{
                matches: [{ field: 'response', condition: '===', value: ACCEPT }],
              }}
              validators={{ required: true }}
            />
            <FormInput
              fullWidth
              type="textarea"
              fieldName="note"
              label="Note (optional)"
              helperText="Anything you'd like the transferor or our team to know"
              defaultValue=""
              validators={{ required: false }}
            />
          </FormPanel>

          <div className="mt-4 flex justify-end">
            <Button loading={acknowledging} onClick={() => formPanelRef.current?.submit()}>
              Submit response
            </Button>
          </div>
        </SectionCard>

        <Dialog
          open={!!pendingResponse}
          onClose={() => setPendingResponse(null)}
          zIndexPriority="highest"
        >
          <DialogPanel>
            <DialogTitle>
              {isDeclining ? 'Decline this transfer?' : 'Accept this transfer?'}
            </DialogTitle>
            <p>
              {isDeclining
                ? 'Declining is final — the transferor will need to raise a new request if they still want to transfer units to you.'
                : 'The units in this transfer will be credited to the entity you selected once our team completes its review.'}
            </p>
            {acknowledgeError ? <ErrorMessage error={acknowledgeError} type="alert" /> : null}
            <DialogActions>
              <Button variant="secondary" onClick={() => setPendingResponse(null)}>
                Cancel
              </Button>
              <Button loading={acknowledging} onClick={confirmResponse}>
                {isDeclining ? 'Decline transfer' : 'Accept transfer'}
              </Button>
            </DialogActions>
          </DialogPanel>
        </Dialog>
      </div>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Respond to transfer request"
        subTitle="Accept or decline the unit transfer request"
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default UnitTransferAcknowledgement;
